import { useEffect, useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import useSEO from "../hooks/useSEO";
import { useAuth } from "../contexts/AuthContext";

const PREFILL_TEMPLATES = {
  role_change: (u) =>
    `Bonjour l'équipe DemkaBiz,\n\nJe souhaite demander un changement de rôle sur mon compte.\n\n• Compte : ${u?.email || ""}\n• Rôle actuel : ${u?.role || ""}\n• Rôle souhaité : (indiquez : client / professionnel / entrepreneur / organisme)\n\nMotif de la demande :\n\nMerci d'avance,\n${u?.name || ""}`,
};

export default function Contact() {
  useSEO({ title: "Contact", description: "Contactez l'équipe DemkaBiz pour toute question, suggestion ou partenariat." });
  const { user } = useAuth();
  const [params] = useSearchParams();
  const prefillKey = params.get("prefill");
  const [form, setForm] = useState({ name: "", email: "", phone: "", description: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Pre-fill name/email from logged-in user
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
      }));
    }
    // Pre-fill message body from template
    if (prefillKey && PREFILL_TEMPLATES[prefillKey]) {
      setForm((f) => ({ ...f, description: f.description || PREFILL_TEMPLATES[prefillKey](user) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, prefillKey]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/requests", { ...form, urgency: "moyenne", contact_method: "email" });
      navigate(`/merci?type=contact&name=${encodeURIComponent(form.name)}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="contact-page">
      <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-2">Contact</p>
      <h1 className="font-display font-bold text-4xl sm:text-5xl text-foreground tracking-tight">Parlons de votre projet</h1>
      <p className="text-muted-foreground mt-2 max-w-2xl">Une question, une suggestion ou un partenariat ? Écrivez-nous.</p>

      <div className="grid lg:grid-cols-3 gap-10 mt-10">
        <div className="space-y-4 lg:col-span-1">
          <div className="card-surface p-5 flex items-start gap-3">
            <Mail className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Email</div>
              <div className="font-medium">contact@demka.ca</div>
            </div>
          </div>
          <div className="card-surface p-5 flex items-start gap-3">
            <Phone className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Téléphone</div>
              <div className="font-medium">+1 (418) 473-5188</div>
            </div>
          </div>
          <div className="card-surface p-5 flex items-start gap-3">
            <MapPin className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Siège social</div>
              <div className="font-medium text-sm leading-relaxed">20, boul. Charest Ouest, Bureau 102<br/>Québec (Québec) G1K 1X2<br/>Canada</div>
            </div>
          </div>
          <div className="card-surface p-5 flex items-start gap-3">
            <MapPin className="w-5 h-5 text-accent mt-0.5" />
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Point focal Afrique</div>
              <div className="font-medium text-sm">Douala – Bonamoussadi, Cameroun<br/>+237 682 52 18 95</div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="card-surface p-7 lg:col-span-2 space-y-4" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Nom complet</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full mt-1 px-4 py-3 border border-emerald-900/10 rounded-xl text-sm outline-none focus:border-primary" data-testid="contact-name" />
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Email</label>
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full mt-1 px-4 py-3 border border-emerald-900/10 rounded-xl text-sm outline-none focus:border-primary" data-testid="contact-email-input" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Téléphone (optionnel)</label>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full mt-1 px-4 py-3 border border-emerald-900/10 rounded-xl text-sm outline-none focus:border-primary" data-testid="contact-phone" />
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Votre message</label>
            <textarea required rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full mt-1 px-4 py-3 border border-emerald-900/10 rounded-xl text-sm outline-none focus:border-primary" data-testid="contact-message" />
          </div>
          <button type="submit" disabled={loading} className="btn-primary" data-testid="contact-submit">
            {loading ? "Envoi…" : "Envoyer"} <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
